import { Vector3 } from '@babylonjs/core/Maths/math.vector';
import { eAxis } from '@models/classes/enums';
import { SketchControlPoint } from '@models/classes/features/sketch-control-point';
import { Kb3dManager } from '@models/classes/kb3d-manager';
import { SketchNode } from '@models/classes/meshes/sketch-node';
import { SketchPath } from '@models/classes/meshes/sketch-node-path';
import { KbVector } from '@models/classes/primitives/kb-vector';
import { Token } from '@models/classes/token';
import { appendOrIncrementNumber } from '@models/classes/utilities';
import { earcut } from '@view/helpers/earcut';
import { SubmeshVertexData, getPolygonNormal } from '@view/helpers/geometry-helper';

export function parseSketchGeometry(vertexData: Partial<SubmeshVertexData> | null) {
    const segments: Vector3[][] = [];
    const holes = new Map<number, Vector3[][]>();

    if (vertexData && vertexData.positions && vertexData.paths) {
        const paths = vertexData.paths;
        const holeIndices = new Map<number, number>();
        if (vertexData.holes) {
            for (let i = 0; i < vertexData.holes.length; i += 2) {
                holeIndices.set(vertexData.holes[i + 1], vertexData.holes[i]);
            }
        }

        let i = 0;
        let currentHole: null | number = null;
        while (i < paths.length) {
            const positions = vertexData.positions;
            const segmentPoints: Vector3[] = [];
            currentHole = holeIndices.has(i) ? holeIndices.get(i)! : null;

            for (; i < paths.length; i += 2) {
                segmentPoints.push(Vector3.FromArray(positions, paths[i] * 3));
                if (paths[i + 2] !== paths[i + 1]) {
                    segmentPoints.push(Vector3.FromArray(positions, paths[i + 1] * 3));
                    i += 2;
                    break;
                }
            }

            if (segmentPoints.length > 1) {
                if (currentHole !== null) {
                    let currentHolePoints = holes.get(currentHole);
                    if (!currentHolePoints) {
                        currentHolePoints = [];
                        holes.set(currentHole, currentHolePoints);
                    }
                    currentHolePoints.push(segmentPoints);
                } else {
                    segments.push(segmentPoints);
                }
            }
        }
    }

    return { segments, holes };
}

export function createSketchPointForEditor(manager: Kb3dManager, path: SketchPath, insertIndex?: number) {
    if (!path._parent) {
        throw new Error('Cannot create a sketch point for a path that has no parent sketch');
    }
    const sketch = path._parent;

    const c: SketchControlPoint = manager.create(Token.SketchControlPoint);
    c.name = 'Control Point';
    while (path.controlPoints.find(ec => ec.name === c.name)) {
        c.name = appendOrIncrementNumber(c.name);
    }

    let previousControlPoint: SketchControlPoint | undefined;
    if (!insertIndex) {
        // If insert index is zero or undefined, determine the previous control point and use its position for the new control point
        if (path.controlPoints.length > 0 && insertIndex === undefined) {
            previousControlPoint = path.controlPoints[path.controlPoints.length - 1];
        } else {
            const currentPathIndex = sketch.paths.findIndex(p => p._dynamicId === path._dynamicId);
            if (currentPathIndex > 0) {
                const previousPath = sketch.paths[currentPathIndex - 1];
                if (previousPath.controlPoints.length > 0) {
                    previousControlPoint = previousPath.controlPoints[previousPath.controlPoints.length - 1];
                }
            }
        }
    } else {
        previousControlPoint = path.controlPoints[insertIndex];
    }

    const sketchNormal = getSketchNormalVector3(sketch.normal);
    // determine the axis making up the sketch plane so we can make sure the direction vectors are on the sketch plane
    let axis1: Vector3;
    if (sketchNormal.equalsWithEpsilon(Vector3.Forward()) || sketchNormal.equalsWithEpsilon(Vector3.Backward())) {
        axis1 = Vector3.Up();
    } else {
        axis1 = Vector3.Cross(sketchNormal, Vector3.Forward());
    }
    const axis2 = Vector3.Cross(sketchNormal, axis1);

    if (previousControlPoint) {
        // if therre is a previous control point, project its direction onto the sketch plane
        c.position = previousControlPoint.position.clone();
        const rawDirection = previousControlPoint.direction.toVec3();
        const projectedDirection = axis1
            .scale(Vector3.Dot(axis1, rawDirection))
            .add(axis2.scale(Vector3.Dot(axis2, rawDirection)));
        c.direction = KbVector.FromVec3(projectedDirection);
    } else {
        // if this is the first control point, just pick a direction vector on the sketch plane
        c.direction = KbVector.FromVec3(axis1);
    }
    c.direction2 = c.direction.scale(-1);

    if (insertIndex === undefined) {
        path.controlPoints.push(c);
    } else {
        path.controlPoints.splice(insertIndex, 0, c);
    }
    for (const eC of path.controlPoints) {
        eC._expanded = false;
    }

    c._visible = true;
    c._expanded = true;

    return c;
}

export function getSketchNormalVector3(normal: KbVector | eAxis) {
    let planeNormal: Vector3;
    switch (normal) {
        case eAxis.x:
            planeNormal = Vector3.Right();
            break;
        case eAxis.y:
            planeNormal = Vector3.Up();
            break;
        case eAxis.z:
            planeNormal = Vector3.Forward();
            break;
        default:
            planeNormal = normal.toVec3();
    }
    planeNormal.normalize();
    return planeNormal;
}

export function projectOnNodeAxis(node: SketchNode, x: number, y: number) {
    switch (node.normal) {
        case eAxis.y:
            return new KbVector(x, 0, -y);
        case eAxis.z:
            return new KbVector(-x, y, 0);
        default:
            return new KbVector(0, y, x);
    }
}

export function axisToVector3(axis: eAxis) {
    switch (axis) {
        case eAxis.y:
            return Vector3.Up();
        case eAxis.z:
            return Vector3.Forward();
        default:
            return Vector3.Right();
    }
}

/**
 * returns the vertices in the order of positionVectors in sequence, followed by holePositions in sequence!
 * Indices will have to be recalculated if the vertex data is replaced with the results of this function.
 */
export function fillSketchPolygon(
    positionVectors: Vector3[],
    flipNormals: boolean,
    sketchNativeNormal: Vector3,
    holePositions?: Vector3[][]
) {
    const sketchNormal = getPolygonNormal(positionVectors);
    if (flipNormals) {
        sketchNormal.scaleInPlace(-1);
    }
    // const geometryNormal = sketchNormal.scale(-1);
    const segmentPositions: number[] = [];
    let segmentIndices: number[] = [];

    for (let i = 0; i < positionVectors.length; i++) {
        const point = positionVectors[i];
        segmentPositions.push(point.x, point.y, point.z);
    }

    const holes: number[] = [];
    if (holePositions) {
        for (let i = 0; i < holePositions.length; i++) {
            const hole = holePositions[i];
            holes.push(segmentPositions.length / 3);
            for (let i = 0; i < hole.length; i++) {
                const point = hole[i];
                segmentPositions.push(point.x, point.y, point.z);
            }
        }
    }

    if (Math.abs(Vector3.Dot(sketchNormal, Vector3.UpReadOnly)) > 0.95) {
        const flatPositions = new Float32Array((segmentPositions.length * 2) / 3);
        const xAxis = Vector3.Cross(sketchNormal, new Vector3(0, 0, 1));
        const yAxis = Vector3.Cross(sketchNormal, xAxis);

        const working = new Vector3();
        for (let i = 0; i < segmentPositions.length; i += 3) {
            working.set(segmentPositions[i], segmentPositions[i + 1], segmentPositions[i + 2]);
            flatPositions.set([Vector3.Dot(working, xAxis), Vector3.Dot(working, yAxis)], (i * 2) / 3);
        }
        const faceNormal = calculateFaceNormalFromFloats(flatPositions, 2);
        if (Vector3.Dot(faceNormal, Vector3.RightHandedForwardReadOnly) < 0) {
            for (let i = 0; i < flatPositions.length; i += 2) {
                const tmp = flatPositions[i];
                flatPositions[i] = flatPositions[i + 1];
                flatPositions[i + 1] = tmp;
            }
            // geometryNormal.scaleInPlace(-1);
        }
        segmentIndices = earcut(flatPositions, holes, 2);
    } else {
        segmentIndices = earcut(segmentPositions, holes, 3);

        // Flip the geometry if needed so that it faces the same direction as the faces because earcut isn't aware of the 3d space
        const faceNormal = calculateFaceNormal(positionVectors);
        // If the rotation handedness is opposite the primary sketch vector, then flip the sketch path
        if (Vector3.Dot(faceNormal, sketchNativeNormal) < 0) {
            for (let i = 0; i < segmentIndices.length; i += 3) {
                const tmp = segmentIndices[i];
                segmentIndices[i] = segmentIndices[i + 2];
                segmentIndices[i + 2] = tmp;
            }
            // geometryNormal.scaleInPlace(-1);
        }
    }

    const segmentNormals: number[] = [];
    const geometryNormal = calculateFaceNormal(positionVectors);
    if (flipNormals) {
        geometryNormal.scaleInPlace(-1);
    }
    let totalVectorCount = positionVectors.length;
    if (holePositions) {
        totalVectorCount += holePositions.flat().length;
    }
    for (let i = 0; i < totalVectorCount; i++) {
        segmentNormals.push(geometryNormal.x, geometryNormal.y, geometryNormal.z);
    }
    const segmentUvs: number[] = [];
    for (let i = 0; i < totalVectorCount; i++) {
        segmentUvs.push(0, 0);
    }

    return {
        normals: segmentNormals,
        positions: segmentPositions,
        indices: segmentIndices,
        uvs: segmentUvs,
    };
}

export function calculateFaceNormal(positionVectors: Vector3[]) {
    const v1 = Vector3.Zero();
    const v2 = Vector3.Zero();
    const barycenter = Vector3.Zero();
    for (let i = 0; i < positionVectors.length; i++) {
        barycenter.addInPlace(positionVectors[i]);
    }
    barycenter.scaleInPlace(1 / positionVectors.length);

    const pathHandedness = Vector3.Zero();
    if (positionVectors.length > 1) {
        for (let iP = 0; iP < positionVectors.length - 1; iP++) {
            const point1 = positionVectors[iP];
            v1.copyFromFloats(point1.x, point1.y, point1.z);
            v1.subtractInPlace(barycenter);
            const point2 = positionVectors[iP + 1];
            v2.copyFromFloats(point2.x, point2.y, point2.z);
            v2.subtractInPlace(barycenter);
            pathHandedness.addInPlace(Vector3.Cross(v1, v2));
        }
        return pathHandedness.normalize();
    }
    return Vector3.Zero();
}

export function calculateFaceNormalFromFloats(points: number[] | Float32Array, dim: number) {
    const v1 = Vector3.Zero();
    const v2 = Vector3.Zero();
    const barycenter = Vector3.Zero();
    for (let i = 0; i < points.length; i += dim) {
        barycenter.addInPlaceFromFloats(points[i], points[i + 1], dim === 3 ? points[i + 2] : 0);
    }
    barycenter.scaleInPlace(3 / points.length);

    const pathHandedness = Vector3.Zero();
    if (points.length > 3) {
        for (let i = 0; i < points.length - dim; i += dim) {
            v1.copyFromFloats(points[i], points[i + 1], dim === 3 ? points[i + 2] : 0);
            v1.subtractInPlace(barycenter);
            v2.copyFromFloats(points[i + dim], points[i + dim + 1], dim === 3 ? points[i + dim + 2] : 0);
            v2.subtractInPlace(barycenter);
            pathHandedness.addInPlace(Vector3.Cross(v1, v2));
        }
        return pathHandedness.normalize();
    }
    return Vector3.Zero();
}
